// ---------------------------------------------------------------------------
// NewConversationView.tsx
//
// Entry point for a brand-new chat with no pre-existing conversation ID.
// Rendered at /chats/new — UUID is only generated on submit, matching the
// same flow as DiscoverView's handleAISubmit.
//
// Route: /chats/new
// ---------------------------------------------------------------------------

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AISearchField } from '../components/AISearchField';

export function NewConversationView() {
    const navigate = useNavigate();

    const handleSubmit = useCallback(
        (query: string) => {
            const conversationId = crypto.randomUUID();
            navigate(`/chat/${conversationId}`, {
                state: { initialQuery: query },
            });
        },
        [navigate]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'background.paper',
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => navigate('/chats')}
                    sx={{ color: 'text.secondary' }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        New Chat
                    </Typography>
                </Box>
            </Box>

            {/* Centered prompt area */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, sm: 3, md: 4 },
                    pb: '10vh',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '20px',
                        fontWeight: 600,
                        height: '20px',
                        color: 'text.primary',
                        mb: 0.75,
                        textAlign: 'center',
                    }}
                ></Typography>
                <Typography
                    sx={{
                        fontSize: '13px',
                        color: 'text.disabled',
                        mb: 3,
                        textAlign: 'center',
                    }}
                >
                    What would you like to explore?
                </Typography>

                <Box sx={{ width: '100%', maxWidth: 640 }}>
                    <AISearchField
                        mode="discovery"
                        onSubmit={handleSubmit}
                        loading={false}
                    />
                </Box>
            </Box>
        </Box>
    );
}
